import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { symbolData, BLANK_SYMBOL_ID } from "../../../SymbolData";
import { Settings, GAME_LAYOUT } from "../../../settings";
import { COIN_SYMBOL_IDS } from "../../../SymbolData";
import { formatToCustomLocale } from "../../../utilities";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";
import { FEATURES_CONFIG } from "../../../featuresConfig";

// Manages overlay animations for winning symbols with pre-positioned spine instances

class OverlayReelController {
  constructor(
    overlaySymbolRefs,
    reelIndex,
    symbolWidth,
    symbolHeight,
    reelContainer = null,
    bet = 1
  ) {
    this.overlaySymbolRefs = overlaySymbolRefs;
    this.reelIndex = reelIndex;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.reelContainer = reelContainer;
    this.bet = bet || 1; // Store bet value for coin calculations
    this.prePositionedSpines = new Map();
    this.activeAnimations = new Map();
    this.animationCache = new Map();

    // ✅ NEW: cache of available animations per spineKey
    this.animationAvailability = new Map();

    // Internal caches for performance optimization
    this._spineScaleCache = new Map(); // Cache for calculateSpineScale results
    this._expandingWildScaleCache = null; // Cache for expanding wild scale
    this._lastSymbolWidth = symbolWidth;
    this._lastSymbolHeight = symbolHeight;
    this._coinDisplayValueCache = new Map(); // Cache for coin display values

    // Expanding wild full-reel spine properties
    this.expandingWildSpine = null;
    this.expandingWildSpineData = {
      skeleton: "wild_expand-skeleton",
      atlas: "wild_expand-atlas",
    };
    this.expandingWildTimeouts = [];
    this.isExpandingWildVisible = false;
    this.expandingWildInitialized = false;
    this.currentExpandingWildAnimation = null; // Track current animation ("appear", "idle", or "win")

    // Spine scaling configuration
    this.expandingWildHeightMultiplier = 1.0;
    this.expandingWildYOffsetMultiplier = 0;

    // Coin value text management (attached to spine bone)
    this.coinValueContainers = new Map(); // symbolIndex → { counterContainer, bitmapTexts, tickerCallback, displayValue }

    // Track pending requestAnimationFrame handles for cleanup
    this._pendingRAF = null;

    this.initializeAllSpineInstances();

    // Initialize and warm up expanding wild spine on Reel 0 only (shader compilation is global)
    // This prevents first-time RAF warnings when expanding wild appears
    if (this.reelIndex === 0 && this.reelContainer) {
      this.initializeExpandingWildSpine();
    }
  }

  getShadowReelController() {
    return this;
  }

  /**
   * Get the expanding wild spine instance for event listening
   * @returns {Spine|null} The expanding wild spine instance
   */
  getExpandingWildSpine() {
    return this.expandingWildSpine;
  }

  /**
   * Calculate display value for coin symbols (coinValue × bet)
   * @param {number} symbolId - Symbol ID
   * @param {number|null} betOverride - Optional bet value for error recovery (ONE TIME use)
   */
  _getCoinDisplayValue(symbolId, betOverride = null) {
    const symbolValue = symbolData[symbolId]?.value;
    if (!symbolValue || symbolValue === null) {
      return "";
    }

    try {
      const coinValue = parseInt(symbolValue, 10);

      if (isNaN(coinValue) || coinValue <= 0) {
        return "";
      }

      // Use bet override if provided (error recovery), otherwise use this.bet
      const bet = betOverride !== null ? betOverride : this.bet;

      const displayValue = coinValue * bet;
      return formatToCustomLocale(displayValue);
    } catch (error) {
      return "";
    }
  }

  /**
   * Create coin value text container with BitmapText
   * Implements progressive scaling based on digit count:
   * - 1-4 digits: Normal size (no scaling)
   * - 5-8 digits: Horizontal squishing only (preserve height)
   * - 9+ digits: Uniform scaling (both width and height)
   */
  createCoinValueText(displayValue) {
    if (!displayValue || displayValue === "") {
      return null;
    }

    const counterContainer = new PIXI.Container();
    counterContainer.name = "coin_value_text";
    counterContainer.alpha = 1;
    counterContainer.zIndex = 1000; // Render above spine effects

    // Calculate font size based on symbol height (use actual runtime height, not constant)
    const fontSize = this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;

    // Create BitmapText for the value
    const bitmapText = new PIXI.BitmapText(displayValue, {
      fontName: "font",
      fontSize: fontSize,
    });

    bitmapText.anchor.set(0.5);
    bitmapText.x = 0;
    bitmapText.y = 0;
    bitmapText.zIndex = 1;
    bitmapText.roundPixels = true;

    // Force update to get accurate measurements
    bitmapText.updateText();

    // Calculate available width (with padding)
    const coinWidth = this.symbolWidth;
    const paddingPercent = 0.12; // 12% padding on each side
    const maxTextWidth = coinWidth * (1 - paddingPercent * 2);

    const actualTextWidth = bitmapText.width;

    // Only apply scaling if text is too wide
    if (actualTextWidth > maxTextWidth) {
      // Count digits (excluding formatting characters like commas and dots)
      const digitCount = displayValue.replace(/[^0-9]/g, '').length;

      // Apply scaling method based on digit count
      if (digitCount <= 4) {
        // 1-4 digits: Horizontal squishing only (shouldn't normally happen, but just in case)
        const scaleX = maxTextWidth / actualTextWidth;
        bitmapText.scale.x = scaleX;
      } else if (digitCount >= 5 && digitCount <= 8) {
        // 5-8 digits: Horizontal squishing only (preserve height)
        const scaleX = maxTextWidth / actualTextWidth;
        bitmapText.scale.x = scaleX; // Only scale width
        // bitmapText.scale.y stays at 1.0 (preserve height)
      } else {
        // 9+ digits: Uniform scaling (both width and height)
        const scale = maxTextWidth / actualTextWidth;
        bitmapText.scale.set(scale); // Scale both X and Y uniformly
      }
    }

    counterContainer.addChild(bitmapText);

    return { counterContainer, bitmapTexts: [bitmapText] };
  }

  /**
   * Sync coin value text transform with spine's "coin_value" bone
   */
  updateCoinValueFromBone(spineInstance, counterContainer) {
    if (!spineInstance || !counterContainer || !spineInstance.skeleton) {
      return;
    }

    const valueBone = spineInstance.skeleton.findBone("coin_value");
    if (!valueBone) {
      return;
    }

    // Apply bone's world transform to counter
    counterContainer.x = valueBone.worldX;
    counterContainer.y = valueBone.worldY;
    counterContainer.rotation = (valueBone.getWorldRotationX() * Math.PI) / 180;

    // Apply bone's animation scale, but counteract the spine's base scale
    // This keeps bone animations working while maintaining correct text size
    const boneScaleX = valueBone.getWorldScaleX();
    const boneScaleY = valueBone.getWorldScaleY();
    const spineScale = spineInstance.scale.x; // Spine's base scale from resizeAnimations()

    if (spineScale && spineScale !== 0) {
      // Bone scale divided by spine scale = animation-only scale at correct size
      counterContainer.scale.set(boneScaleX / spineScale, boneScaleY / spineScale);
    } else {
      counterContainer.scale.set(boneScaleX, boneScaleY);
    }
  }

  /**
   * Start bone update loop for coin value text
   */
  startCoinValueBoneUpdate(symbolIndex, spineInstance, counterContainer) {
    // Create ticker callback
    const tickerCallback = () => {
      this.updateCoinValueFromBone(spineInstance, counterContainer);
    };

    // Add to ticker
    PIXI.Ticker.shared.add(tickerCallback);

    // Store reference
    const existing = this.coinValueContainers.get(symbolIndex);
    if (existing) {
      existing.tickerCallback = tickerCallback;
    }

    // Initial update
    this.updateCoinValueFromBone(spineInstance, counterContainer);
  }

  /**
   * Stop bone update loop for coin value text
   */
  stopCoinValueBoneUpdate(symbolIndex) {
    const coinData = this.coinValueContainers.get(symbolIndex);
    if (coinData && coinData.tickerCallback) {
      PIXI.Ticker.shared.remove(coinData.tickerCallback);
      coinData.tickerCallback = null;
    }
  }

  /**
   * Show coin value text for a coin symbol
   * @param {number} symbolIndex - Position index
   * @param {number} symbolId - Symbol ID
   * @param {object} spineInstance - Spine instance
   * @param {number|null} betOverride - Optional bet value for error recovery
   */
  showCoinValueText(symbolIndex, symbolId, spineInstance, betOverride = null) {
    // Check if it's a coin symbol
    if (!COIN_SYMBOL_IDS.includes(symbolId)) {
      return;
    }

    // Check if coin value text already exists for this symbol at this position
    const existingCoinData = this.coinValueContainers.get(symbolIndex);
    if (existingCoinData && existingCoinData.counterContainer && !existingCoinData.counterContainer.destroyed) {
      // Coin value text already exists, don't recreate it
      return;
    }

    // Hide existing coin value text if any (cleanup destroyed containers)
    this.hideCoinValueText(symbolIndex);

    // Calculate display value (use bet override for recovery, otherwise current bet)
    const displayValue = this._getCoinDisplayValue(symbolId, betOverride);
    if (!displayValue || displayValue === "") {
      return;
    }

    // Create coin value text
    const textData = this.createCoinValueText(displayValue);
    if (!textData) {
      return;
    }

    const { counterContainer, bitmapTexts } = textData;

    // Add to spine instance
    spineInstance.addChild(counterContainer);
    spineInstance.sortableChildren = true;

    // Store reference with the display value (immutable once created)
    this.coinValueContainers.set(symbolIndex, {
      counterContainer,
      bitmapTexts,
      tickerCallback: null,
      displayValue, // Store the value so it never changes during animation
    });

    // Start bone update loop
    this.startCoinValueBoneUpdate(symbolIndex, spineInstance, counterContainer);
  }

  /**
   * Hide coin value text for a symbol
   */
  hideCoinValueText(symbolIndex) {
    const coinData = this.coinValueContainers.get(symbolIndex);
    if (!coinData) {
      return;
    }

    // Stop ticker
    this.stopCoinValueBoneUpdate(symbolIndex);

    // Destroy bitmap texts
    if (coinData.bitmapTexts) {
      coinData.bitmapTexts.forEach((text) => {
        if (text && !text.destroyed) {
          text.destroy();
        }
      });
    }

    // Destroy container
    if (coinData.counterContainer && !coinData.counterContainer.destroyed) {
      if (coinData.counterContainer.parent) {
        coinData.counterContainer.parent.removeChild(coinData.counterContainer);
      }
      coinData.counterContainer.destroy({ children: true });
    }

    // Remove from map
    this.coinValueContainers.delete(symbolIndex);
  }

  /**
   * Hide all coin value texts
   */
  hideAllCoinValueTexts() {
    const symbolIndices = Array.from(this.coinValueContainers.keys());
    symbolIndices.forEach((symbolIndex) => {
      this.hideCoinValueText(symbolIndex);
    });
  }

  /**
   * Update bet value and refresh all visible coin value texts
   */
  updateBet(newBet) {
    const oldBet = this.bet;
    this.bet = newBet || 1; // Store new bet value for next spin

    // Invalidate coin display value cache when bet changes
    if (oldBet !== this.bet) {
      this._coinDisplayValueCache.clear();
    }

    // Don't update coin values on reels - they belong to a completed spin round
    // Coin values will update when new symbols land on next spin
    // Note: Paytable/info overlay update separately (not affected by this)
    return;
  }

  /**
   * ✅ Combined and optimized method for checking available animations.
   * Returns object with hasLanding, hasIdle, and hasAppear using cached data.
   */
  getAvailableAnimations(symbolIndex, symbolId) {
    // Texture-only game optimization - skip landing/idle checks if disabled
    if (!FEATURES_CONFIG.SPINE_ANIMATIONS.LANDING_ENABLED && 
        !FEATURES_CONFIG.SPINE_ANIMATIONS.IDLE_ENABLED) {
      return { hasLanding: false, hasIdle: false, hasAppear: true };
    }

    // Always look up using position 0 since all positions share the same symbol's animations
    const warmupKey = `0_${symbolId}`;
    const available = this.animationAvailability.get(warmupKey);

    if (!available) {
      return { hasLanding: false, hasIdle: false, hasAppear: false };
    }

    let hasLanding = false;
    let hasIdle = false;
    let hasAppear = false;

    for (const name of available) {
      if (!hasLanding && name.includes("land")) hasLanding = true;
      if (!hasIdle && name.includes("idle")) hasIdle = true;
      if (!hasAppear && name.includes("appear")) hasAppear = true;
      if (hasLanding && hasIdle && hasAppear) break;
    }

    // Apply feature flags to results
    return { 
      hasLanding: FEATURES_CONFIG.SPINE_ANIMATIONS.LANDING_ENABLED && hasLanding, 
      hasIdle: FEATURES_CONFIG.SPINE_ANIMATIONS.IDLE_ENABLED && hasIdle, 
      hasAppear 
    };
  }

  // ✅ hasIdleAnimationAt now uses cached availability instead of scanning animations
  hasIdleAnimationAt(symbolIndex, symbolId) {
    const { hasIdle } = this.getAvailableAnimations(symbolIndex, symbolId);
    return hasIdle;
  }

  // Show win animation using pre-positioned spine instances
  // @param {number|null} betOverride - Optional bet value for error recovery
  showWinAnimation(symbolIndex, symbolId, animationType = "win", betOverride = null) {
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) return;

    const spineKey = `${symbolIndex}_${symbolId}`;
    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) return;

    const existingAnimation = this.activeAnimations.get(symbolIndex);

    // Early return if SAME symbol and animation type already playing
    // Prevents flicker on shared symbols across multiple winning lines during cycling
    if (
      existingAnimation &&
      existingAnimation.symbolId === symbolId &&
      existingAnimation.animationType === animationType
    ) {
      // Symbol already playing - don't restart! Just ensure visibility
      spineInstance.alpha = 1;
      overlaySymbol.alpha = 1;
      return;
    }

    // Only hide if DIFFERENT symbol or animation type
    if (existingAnimation) {
      this.hideWinAnimation(symbolIndex);
    }

    spineInstance.alpha = 1;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;

    const selectedAnimation = this.selectSpineAnimation(
      spineInstance,
      animationType,
      symbolId
    );

    // Restart animation from frame 0 to ensure synchronization
    // NOTE: Do NOT call setToSetupPose() here — it triggers spine-pixi-v7 to
    // recreate internal DarkSlotMesh/DarkTintMaterial/DarkTintGeometry objects
    // that accumulate in memory. clearTracks() + addAnimation() is sufficient.
    if (spineInstance.state && selectedAnimation) {
      spineInstance.state.clearTracks();
      spineInstance.state.addAnimation(0, selectedAnimation, true, 0);
    }

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType,
    });

    // Show coin value text if this is a coin symbol (pass bet override for recovery)
    this.showCoinValueText(symbolIndex, symbolId, spineInstance, betOverride);
  }

  /**
   * Sync/restart win animation to frame 0 without hiding (keeps animations in sync across lines)
   * Unlike showWinAnimation, this ALWAYS restarts even if same animation is playing
   */
  syncWinAnimation(symbolIndex, symbolId, animationType = "win", betOverride = null) {
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) return;

    const spineKey = `${symbolIndex}_${symbolId}`;
    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) return;

    // Ensure visibility
    spineInstance.alpha = 1;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;

    const selectedAnimation = this.selectSpineAnimation(
      spineInstance,
      animationType,
      symbolId
    );

    // Reset animation to frame 0 without hiding
    if (spineInstance.state && selectedAnimation) {
      // Get current track (if exists)
      const currentTrack = spineInstance.state.tracks[0];
      
      // If already playing the same animation, just reset time
      if (currentTrack && currentTrack.animation.name === selectedAnimation) {
        currentTrack.trackTime = 0;
      } else {
        // Different animation, need to switch
        spineInstance.state.clearTracks();
        spineInstance.state.addAnimation(0, selectedAnimation, true, 0);
      }
    }

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType,
    });

    // Show coin value text if this is a coin symbol
    this.showCoinValueText(symbolIndex, symbolId, spineInstance, betOverride);
  }

  // ✅ Show landing animation using cached availability
  // @param {number|null} betOverride - Optional bet value for error recovery
  showLandingAnimation(symbolIndex, symbolId, betOverride = null) {
    const spineKey = `${symbolIndex}_${symbolId}`;

    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) {
      return;
    }

    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) {
      return;
    }

    const { hasLanding, hasIdle } = this.getAvailableAnimations(
      symbolIndex,
      symbolId
    );

    if (!hasLanding && !hasIdle) {
      return;
    }

    const existingAnimation = this.activeAnimations.get(symbolIndex);
    if (existingAnimation && existingAnimation.animationType === "win") {
      this.hideWinAnimation(symbolIndex);
    }

    spineInstance.alpha = 1;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;

    // No setup pose needed here — hideWinAnimation() already calls setToSetupPose()
    // when the spine goes inactive, ensuring a clean slate for next show

    if (hasLanding && hasIdle) {
      const landingAnimation = this.selectSpineAnimation(
        spineInstance,
        "land",
        symbolId
      );
      const idleAnimation = this.selectSpineAnimation(
        spineInstance,
        "idle",
        symbolId
      );

      if (spineInstance.state && landingAnimation && idleAnimation) {
        spineInstance.state.clearTracks();
        spineInstance.state.addAnimation(0, landingAnimation, false, 0);
        spineInstance.state.addAnimation(0, idleAnimation, true, 0);
      }
    } else if (hasIdle) {
      const idleAnimation = this.selectSpineAnimation(
        spineInstance,
        "idle",
        symbolId
      );
      if (spineInstance.state && idleAnimation) {
        spineInstance.state.clearTracks();
        spineInstance.state.addAnimation(0, idleAnimation, true, 0);
      }
    } else if (hasLanding) {
      const landingAnimation = this.selectSpineAnimation(
        spineInstance,
        "land",
        symbolId
      );
      if (spineInstance.state && landingAnimation) {
        spineInstance.state.clearTracks();
        spineInstance.state.addAnimation(0, landingAnimation, true, 0);
      }
    }

    const recordedType =
      hasLanding && hasIdle ? "idle" : hasIdle ? "idle" : "landing";

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType: recordedType,
      hadLandingAnimation: hasLanding,
    });

    // Show coin value text if this is a coin symbol (pass bet override for recovery)
    this.showCoinValueText(symbolIndex, symbolId, spineInstance, betOverride);
  }

  // Show idle animation (unchanged, uses cache internally now)
  // @param {number|null} betOverride - Optional bet value for error recovery
  showIdleAnimation(symbolIndex, symbolId, betOverride = null) {
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) return;

    const spineKey = `${symbolIndex}_${symbolId}`;
    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) return;

    const { hasIdle } = this.getAvailableAnimations(symbolIndex, symbolId);
    if (!hasIdle) return;

    spineInstance.alpha = 1;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;

    // No setup pose needed here — hideWinAnimation() already calls setToSetupPose()
    // when the spine goes inactive, ensuring a clean slate for next show

    const idleAnimation = this.selectSpineAnimation(
      spineInstance,
      "idle",
      symbolId
    );
    if (spineInstance.state && idleAnimation) {
      spineInstance.state.clearTracks();
      spineInstance.state.addAnimation(0, idleAnimation, true, 0);
    }

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType: "idle",
    });

    // Show coin value text if this is a coin symbol (pass bet override for recovery)
    this.showCoinValueText(symbolIndex, symbolId, spineInstance, betOverride);
  }

  // Show appear animation (used when symbol first appears, may fallback to idle or win)
  // @param {number|null} betOverride - Optional bet value for error recovery
  showAppearAnimation(symbolIndex, symbolId, betOverride = null) {
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) return;

    const spineKey = `${symbolIndex}_${symbolId}`;
    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) return;

    const { hasAppear, hasIdle, hasLanding } = this.getAvailableAnimations(
      symbolIndex,
      symbolId
    );

    // If no animations available, don't show anything
    if (!hasAppear && !hasIdle && !hasLanding) return;

    spineInstance.alpha = 1;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;

    let selectedAnimation = null;

    // Try appear first, fallback to idle, then landing
    if (hasAppear) {
      selectedAnimation = this.selectSpineAnimation(
        spineInstance,
        "appear",
        symbolId
      );
    } else if (hasIdle) {
      selectedAnimation = this.selectSpineAnimation(
        spineInstance,
        "idle",
        symbolId
      );
    } else if (hasLanding) {
      selectedAnimation = this.selectSpineAnimation(
        spineInstance,
        "land",
        symbolId
      );
    }

    // No setup pose needed here — hideWinAnimation() already calls setToSetupPose()
    // when the spine goes inactive, ensuring a clean slate for next show
    if (spineInstance.state && selectedAnimation) {
      spineInstance.state.clearTracks();
      spineInstance.state.addAnimation(0, selectedAnimation, true, 0);
    }

    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId,
      animationType: "appear",
    });

    // Show coin value text if this is a coin symbol (pass bet override for recovery)
    this.showCoinValueText(symbolIndex, symbolId, spineInstance, betOverride);
  }

  hideWinAnimation(symbolIndex) {
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    const animationData = this.activeAnimations.get(symbolIndex);
    if (!animationData || !overlaySymbol) return;

    const { spineInstance } = animationData;
    spineInstance.alpha = 0.001;
    spineInstance.autoUpdate = false;
    overlaySymbol.alpha = 0.001;

    // Full skeleton reset on hide — called at low frequency (once per hide transition)
    // Cleans up slot attachments and bone state so next show* starts from clean slate
    // Safe here because spine is invisible (alpha 0.001) and autoUpdate is false
    if (spineInstance.state) {
      spineInstance.state.clearTracks();
    }
    if (spineInstance.skeleton?.setToSetupPose) {
      spineInstance.skeleton.setToSetupPose();
    }

    this.activeAnimations.delete(symbolIndex);

    // Hide coin value text if any
    this.hideCoinValueText(symbolIndex);
  }

  hideLandingAnimation(symbolIndex) {
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    const animationData = this.activeAnimations.get(symbolIndex);
    if (!animationData || !overlaySymbol) return;

    if (animationData.animationType !== "landing") return;

    const { spineInstance } = animationData;
    spineInstance.alpha = 0.001;
    spineInstance.autoUpdate = false;
    overlaySymbol.alpha = 0.001;

    // Full skeleton reset on hide — same pattern as hideWinAnimation
    if (spineInstance.state) {
      spineInstance.state.clearTracks();
    }
    if (spineInstance.skeleton?.setToSetupPose) {
      spineInstance.skeleton.setToSetupPose();
    }

    this.activeAnimations.delete(symbolIndex);
  }

  hideAllAnimations(exceptPositions = null) {
    // First: hide tracked animations (the fast path)
    const activeIndexes = Array.from(this.activeAnimations.keys());
    activeIndexes.forEach((symbolIndex) => {
      if (exceptPositions && exceptPositions.has(symbolIndex)) {
        return;
      }
      this.hideWinAnimation(symbolIndex);
    });

    // Hide all coin value texts (they should already be hidden by hideWinAnimation, but ensure cleanup)
    this.hideAllCoinValueTexts();

    // CRITICAL: Full sweep of ALL pre-positioned spines to catch orphaned autoUpdate=true
    // Without this, spines that lost their activeAnimations entry (race conditions in async win flow)
    // remain ticking on every frame forever, causing gradual CPU accumulation in turbo mode
    if (!exceptPositions) {
      for (const spine of this.prePositionedSpines.values()) {
        if (spine && spine.autoUpdate) {
          spine.autoUpdate = false;
          spine.alpha = 0.001;
          if (spine.state) {
            spine.state.clearTracks();
          }
        }
      }
      this.activeAnimations.clear();
    }
  }

  hideAllAnimationsExcept(upcomingPositions = []) {
    if (!upcomingPositions || upcomingPositions.length === 0) {
      this.hideAllAnimations();
      return;
    }

    // Skip if no animations are active on this reel
    if (this.activeAnimations.size === 0) return;

    const preserveMap = new Map();
    for (let i = 0; i < upcomingPositions.length; i++) {
      const [reelIndex, symbolIndex, symbolId] = upcomingPositions[i];
      if (reelIndex === this.reelIndex) {
        preserveMap.set(symbolIndex, symbolId);
      }
    }

    for (const [symbolIndex, animationData] of this.activeAnimations) {
      const preservedSymbolId = preserveMap.get(symbolIndex);
      if (preservedSymbolId !== animationData.symbolId) {
        this.hideWinAnimation(symbolIndex);
      }
    }
  }

  calculateSpineScale(
    spineInstance,
    containerWidth,
    containerHeight,
    skipAnimationSetup = false
  ) {
    if (
      !skipAnimationSetup &&
      spineInstance &&
      spineInstance.state &&
      spineInstance.state.data &&
      spineInstance.state.data.skeletonData.animations.length > 0
    ) {
      const defaultAnimation =
        spineInstance.state.data.skeletonData.animations[0];
      spineInstance.state.addAnimation(0, defaultAnimation.name, true, 0);
    }

    // Check cache first
    const cacheKey = `${containerWidth}_${containerHeight}`;
    if (this._spineScaleCache.has(cacheKey)) {
      return this._spineScaleCache.get(cacheKey);
    }

    const nativeWidth = Settings.SYMBOL_WIDTH;
    const nativeHeight = Settings.SYMBOL_HEIGHT;
    const heightScale = containerHeight / nativeHeight;
    const scale = heightScale;

    const scaledWidth = nativeWidth * scale;
    const scaledHeight = nativeHeight * scale;
    const x = (containerWidth - scaledWidth) / 2;
    const y = (containerHeight - scaledHeight) / 2;

    const result = {
      scale,
      nativeWidth,
      nativeHeight,
      x,
      y,
      scaledWidth,
      scaledHeight,
    };

    // Cache result
    this._spineScaleCache.set(cacheKey, result);

    return result;
  }

  resizeAnimations(newSymbolWidth, newSymbolHeight) {
    if (
      this.symbolWidth === newSymbolWidth &&
      this.symbolHeight === newSymbolHeight
    ) {
      return;
    }

    this.symbolWidth = newSymbolWidth;
    this.symbolHeight = newSymbolHeight;

    // Invalidate caches when dimensions change
    this._spineScaleCache.clear();
    this._expandingWildScaleCache = null;
    this._coinDisplayValueCache.clear(); // Clear coin cache on resize (bet might have changed)
    this._lastSymbolWidth = newSymbolWidth;
    this._lastSymbolHeight = newSymbolHeight;

    const nativeHeight = Settings.SYMBOL_HEIGHT;
    const heightScale = newSymbolHeight / nativeHeight;
    const scaleRatio = heightScale;

    for (const spineInstance of this.prePositionedSpines.values()) {
      if (spineInstance?.scale) {
        spineInstance.scale.set(scaleRatio);
      }
    }

    for (let i = 0; i < this.overlaySymbolRefs.length; i++) {
      const overlaySymbol = this.overlaySymbolRefs[i];
      if (overlaySymbol) {
        overlaySymbol.y = newSymbolHeight * i;
      }
    }

    // Update coin value text font sizes on resize (use stored displayValue, never recalculate)
    this.coinValueContainers.forEach((coinData, symbolIndex) => {
      if (coinData.bitmapTexts && coinData.bitmapTexts.length > 0) {
        coinData.bitmapTexts.forEach((bitmapText) => {
          if (bitmapText && !bitmapText.destroyed) {
            // Use the stored display value (immutable, set when animation was created)
            const displayValue = coinData.displayValue || bitmapText.text;

            // Calculate new font size based on updated symbol height
            const fontSize = this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
            bitmapText.fontSize = fontSize;

            // Reset scale first
            bitmapText.scale.set(1);

            // Force update to get accurate measurements with new font size
            bitmapText.updateText();
            bitmapText.dirty = true;

            // Recalculate scaling with new dimensions
            const coinWidth = this.symbolWidth;
            const paddingPercent = 0.12;
            const maxTextWidth = coinWidth * (1 - paddingPercent * 2);
            const actualTextWidth = bitmapText.width;

            // Only apply scaling if text is too wide
            if (actualTextWidth > maxTextWidth) {
              const digitCount = displayValue.replace(/[^0-9]/g, '').length;

              if (digitCount <= 4) {
                const scaleX = maxTextWidth / actualTextWidth;
                bitmapText.scale.x = scaleX;
              } else if (digitCount >= 5 && digitCount <= 8) {
                const scaleX = maxTextWidth / actualTextWidth;
                bitmapText.scale.x = scaleX;
              } else {
                const scale = maxTextWidth / actualTextWidth;
                bitmapText.scale.set(scale);
              }
            }
          }
        });
      }
    });

    // Resize expanding wild spine if initialized
    if (this.expandingWildInitialized) {
      this.applyExpandingWildScaling();
    }
  }

  createSpineInstance(symbolId) {
    const symbolInfo = symbolData[symbolId];
    if (!symbolInfo?.spine || symbolInfo.spine === "") return null;

    const spineName = symbolInfo.spine;
    const skeletonAlias = `${spineName}-skeleton`;
    const atlasAlias = `${spineName}-atlas`;
    const assets = PIXI.Assets.cache;

    if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) return null;

    try {
      return Spine.from({ skeleton: skeletonAlias, atlas: atlasAlias, autoUpdate: false });
    } catch {
      return null;
    }
  }

  selectSpineAnimation(spineInstance, animationType, symbolId) {
    const cacheKey = `${symbolId}_${animationType}`;
    let cachedAnimation = this.animationCache.get(cacheKey);
    if (cachedAnimation !== undefined) return cachedAnimation;

    if (!spineInstance.skeleton?.data?.animations) {
      this.animationCache.set(cacheKey, null);
      return null;
    }

    const animations = spineInstance.skeleton.data.animations;
    let selectedAnimation = null;

    if (animationType === "auto") {
      selectedAnimation = animations[0]?.name || null;
    } else {
      const exactMatch = animations.find((anim) =>
        anim.name.toLowerCase().includes(animationType.toLowerCase())
      );
      selectedAnimation = exactMatch
        ? exactMatch.name
        : animations[0]?.name || null;
    }

    this.animationCache.set(cacheKey, selectedAnimation);
    return selectedAnimation;
  }

  _smoothTransitionToAnimation(
    spineInstance,
    newAnimationType,
    symbolId,
    loop = true
  ) {
    if (!spineInstance || !spineInstance.state) return false;

    const newAnimation = this.selectSpineAnimation(
      spineInstance,
      newAnimationType,
      symbolId
    );
    if (!newAnimation) return false;

    try {
      const currentTracks = spineInstance.state.tracks;
      const hasCurrentAnimation =
        currentTracks && currentTracks.length > 0 && currentTracks[0];

      if (hasCurrentAnimation) {
        const currentTrack = currentTracks[0];
        const currentAnimationName = currentTrack.animation
          ? currentTrack.animation.name
          : null;

        if (currentAnimationName === newAnimation) return true;
        spineInstance.state.addAnimation(0, newAnimation, loop, 0.1);
      } else {
        spineInstance.state.setAnimation(0, newAnimation, loop);
      }
      return true;
    } catch {
      return false;
    }
  }

  async initializeAllSpineInstances() {
    this.createAllPrePositionedSpines();
  }

  createAllPrePositionedSpines() {
    const WARMUP_POSITION = 0; // Only warm up in first symbol position
    const WARMUP_REEL = 0; // Only warm up in first reel

    for (
      let symbolIndex = 0;
      symbolIndex < GAME_LAYOUT.SYMBOLS_VISIBLE;
      symbolIndex++
    ) {
      const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
      if (!overlaySymbol) continue;

      for (
        let symbolId = 0;
        symbolId < Object.keys(symbolData).length;
        symbolId++
      ) {
        // Conditionally exclude blank symbols for Hold and Win mode
        // BLANK_SYMBOL_ID (0): In base game = wild (w1), in Hold and Win = blank space
        const shouldExcludeBlank = FEATURES_CONFIG.HOLD_AND_WIN?.ENABLED && symbolId === BLANK_SYMBOL_ID;
        
        if (shouldExcludeBlank) {
          continue;
        }

        const symbolInfo = symbolData[symbolId];
        // Early check: skip if symbol doesn't exist or has no valid spine
        if (
          !symbolInfo ||
          !symbolInfo.spine ||
          symbolInfo.spine === null ||
          symbolInfo.spine === "" ||
          typeof symbolInfo.spine !== "string"
        ) {
          continue;
        }

        let spineInstance;

        // Only create and warm up spine in first reel, first position
        if (this.reelIndex === WARMUP_REEL && symbolIndex === WARMUP_POSITION) {
          spineInstance = this.createSpineInstance(symbolId);
          if (!spineInstance) continue;

          const scaleData = this.calculateSpineScale(
            spineInstance,
            this.symbolWidth,
            this.symbolHeight,
            true
          );

          spineInstance.position.set(scaleData.x, scaleData.y);
          spineInstance.scale.set(scaleData.scale);
          spineInstance.zIndex = 10;
          spineInstance.alpha = 0.001;
          overlaySymbol.addChild(spineInstance);

          // ✅ Build animation availability cache from warmed-up spine
          const key = `${symbolIndex}_${symbolId}`;
          if (spineInstance.skeleton?.data?.animations?.length > 0) {
            const available = new Set(
              spineInstance.skeleton.data.animations.map((a) =>
                a.name.toLowerCase()
              )
            );
            // Only cache if there are actual animations
            if (available.size > 0) {
              this.animationAvailability.set(key, available);
            }
          }

          // Warm up animations (shader compilation + GPU buffer upload happens here)
          // Order matters: each setAnimation replaces the previous animation's meshes.
          // "win" is warmed LAST so its meshes survive in the display list after cleanup.
          this.warmUpSpineAnimation(spineInstance, symbolId, "appear");
          this.warmUpSpineAnimation(spineInstance, symbolId, "idle");
          this.warmUpSpineAnimation(spineInstance, symbolId, "land");
          this.warmUpSpineAnimation(spineInstance, symbolId, "win");

          // Schedule cleanup after GPU has rendered all animation meshes (3 frames)
          this.scheduleWarmupCleanup(spineInstance);

          const spineKey = `${symbolIndex}_${symbolId}`;
          this.prePositionedSpines.set(spineKey, spineInstance);
        } else {
          // For all other reels and positions, create fresh instances (shaders already compiled)
          spineInstance = this.createSpineInstance(symbolId);
          if (!spineInstance) continue;

          const scaleData = this.calculateSpineScale(
            spineInstance,
            this.symbolWidth,
            this.symbolHeight,
            true
          );

          spineInstance.position.set(scaleData.x, scaleData.y);
          spineInstance.scale.set(scaleData.scale);
          spineInstance.zIndex = 10;
          spineInstance.alpha = 0.001;
          overlaySymbol.addChild(spineInstance);

          // ✅ Build animation availability cache from this spine instance
          // All positions share the same symbol's animations, so use position 0 key for lookup
          const warmupKey = `${WARMUP_POSITION}_${symbolId}`;

          // Check if we already have the cache entry for this symbol (from position 0)
          let available = this.animationAvailability.get(warmupKey);

          // If not, populate it from this spine instance
          if (
            !available &&
            spineInstance.skeleton?.data?.animations?.length > 0
          ) {
            available = new Set(
              spineInstance.skeleton.data.animations.map((a) =>
                a.name.toLowerCase()
              )
            );
            // Only cache if there are actual animations
            if (available.size > 0) {
              this.animationAvailability.set(warmupKey, available);
            }
          }

          // Warm up GPU mesh buffers for all animation types (prevents first-use jitter)
          // "win" is warmed LAST so its meshes survive after cleanup
          this.warmUpSpineAnimation(spineInstance, symbolId, "appear");
          this.warmUpSpineAnimation(spineInstance, symbolId, "idle");
          this.warmUpSpineAnimation(spineInstance, symbolId, "land");
          this.warmUpSpineAnimation(spineInstance, symbolId, "win");
          this.scheduleWarmupCleanup(spineInstance);

          const spineKey = `${symbolIndex}_${symbolId}`;
          this.prePositionedSpines.set(spineKey, spineInstance);
        }
      }
    }
  }

  warmUpSpineAnimation(spineInstance, symbolId, animationType = "win") {
    if (!spineInstance?.state) return;
    try {
      const animation = this.selectSpineAnimation(
        spineInstance,
        animationType,
        symbolId
      );
      if (!animation) return;

      // Use setAnimation (replaces current) instead of clearTracks + addAnimation.
      // This preserves the previous animation's mesh data until the new one replaces it,
      // giving the GPU a chance to render each animation's mesh before it's replaced.
      spineInstance.state.setAnimation(0, animation, false);
      spineInstance.state.update(0.016);
      spineInstance.skeleton.updateWorldTransform();

      spineInstance.alpha = 0.001;
      const overlaySymbol = spineInstance.parent;
      if (overlaySymbol) overlaySymbol.alpha = 0.001;
    } catch {}
  }

  /**
   * Finalize warmup cleanup after GPU has had time to render all animation meshes.
   * Called once per spine AFTER all warmUpSpineAnimation calls complete.
   */
  scheduleWarmupCleanup(spineInstance) {
    if (!spineInstance?.state) return;

    const overlaySymbol = spineInstance.parent;

    // Wait 3 frames — ensures PIXI's renderer has drawn the spine mesh
    // and uploaded vertex/texture data to the GPU for all warmed-up animations
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (spineInstance.state) {
            // Clear tracks after GPU has rendered all warmed-up meshes (3 frames).
            // Mesh GPU buffers are already cached — clearTracks() just resets track state
            // so the first showWinAnimation() starts from a clean slate.
            spineInstance.state.clearTracks();
            spineInstance.autoUpdate = false;
          }
          spineInstance.alpha = 0.001;
          if (overlaySymbol) overlaySymbol.alpha = 0.001;
        });
      });
    });
  }

  forceDeepSpineCleanup() {
    for (const spineInstance of this.prePositionedSpines.values()) {
      if (spineInstance?.state) {
        spineInstance.state.clearTracks();
        spineInstance.autoUpdate = false;
        spineInstance.alpha = 0.001;
      }
    }

    for (let i = 0; i < this.overlaySymbolRefs.length; i++) {
      const overlaySymbol = this.overlaySymbolRefs[i];
      if (overlaySymbol?.alpha > 0) {
        overlaySymbol.alpha = 0.001;
      }
    }

    this.activeAnimations.clear();
  }

  /**
   * Show appear animation at a specific position (used for cascading expanding wilds)
   * @param {number} symbolIndex - Position index (0-3)
   * @param {number} targetSymbolId - Symbol ID to show (typically wild = 0)
   */
  showAppearAnimationAtPosition(symbolIndex, targetSymbolId) {
    const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
    if (!overlaySymbol) {
      return;
    }

    // Get the pre-positioned spine instance for this position with target symbol
    const spineKey = `${symbolIndex}_${targetSymbolId}`;
    const spineInstance = this.prePositionedSpines.get(spineKey);
    if (!spineInstance) {
      return;
    }

    // Hide any existing animation at this position
    const existingAnimation = this.activeAnimations.get(symbolIndex);
    if (existingAnimation) {
      this.hideWinAnimation(symbolIndex);
    }

    // Make spine and overlay visible
    spineInstance.alpha = 1;
    spineInstance.autoUpdate = true;
    overlaySymbol.alpha = 1;

    // Play appear animation (fallback to land if appear not available)
    let appearAnimation = this.selectSpineAnimation(
      spineInstance,
      "appear",
      targetSymbolId
    );

    // If no appear animation found, try land as fallback
    if (!appearAnimation || appearAnimation === spineInstance.skeleton?.data?.animations?.[0]?.name) {
      const landAnim = this.selectSpineAnimation(spineInstance, "land", targetSymbolId);
      if (landAnim) appearAnimation = landAnim;
    }

    if (spineInstance.state && appearAnimation) {
      // Use setBonesToSetupPose() instead of setToSetupPose() — resets bones without slot attachment changes (no DarkSlotMesh leak)
      spineInstance.skeleton.setBonesToSetupPose();
      spineInstance.state.clearTracks();
      // Play appear animation once (no loop) - will stop at last frame when finished
      spineInstance.state.addAnimation(0, appearAnimation, false, 0);
    }

    // Track this as an active animation
    this.activeAnimations.set(symbolIndex, {
      spineInstance,
      symbolId: targetSymbolId,
      animationType: "appear",
    });

    // Show coin value text if this is a coin symbol
    this.showCoinValueText(symbolIndex, targetSymbolId, spineInstance);
  }

  /**
   * Replace visible symbol animations with target symbol appear animation
   * @param {number} targetSymbolId - Symbol ID to replace visible symbols with
   */
  replaceVisibleSymbols(targetSymbolId) {
    // Use GAME_LAYOUT indices to loop through visible positions
    for (const symbolIndex of GAME_LAYOUT.SYMBOL_INDICES) {
      const overlaySymbol = this.overlaySymbolRefs[symbolIndex];
      if (!overlaySymbol) {
        continue;
      }

      // Get the pre-positioned spine instance for this position with target symbol
      const spineKey = `${symbolIndex}_${targetSymbolId}`;
      const spineInstance = this.prePositionedSpines.get(spineKey);
      if (!spineInstance) {
        continue;
      }

      // Hide any existing animation at this position
      const existingAnimation = this.activeAnimations.get(symbolIndex);
      if (existingAnimation) {
        this.hideWinAnimation(symbolIndex);
      }

      // Make spine and overlay visible
      spineInstance.alpha = 1;
      spineInstance.autoUpdate = true;
      overlaySymbol.alpha = 1;

      // Play appear animation (fallback to land if appear not available)
      let appearAnimation = this.selectSpineAnimation(
        spineInstance,
        "appear",
        targetSymbolId
      );

      // If no appear animation found, try land as fallback
      if (!appearAnimation || appearAnimation === spineInstance.skeleton?.data?.animations?.[0]?.name) {
        const landAnim = this.selectSpineAnimation(spineInstance, "land", targetSymbolId);
        if (landAnim) appearAnimation = landAnim;
      }

      if (spineInstance.state && appearAnimation) {
        spineInstance.state.clearTracks();
        spineInstance.state.addAnimation(0, appearAnimation, true, 0);
      }

      // Track this as an active animation
      this.activeAnimations.set(symbolIndex, {
        spineInstance,
        symbolId: targetSymbolId,
        animationType: "appear",
      });

      // Show coin value text if this is a coin symbol
      this.showCoinValueText(symbolIndex, targetSymbolId, spineInstance);
    }
  }

  /**
   * Create expanding wild spine instance
   */
  createExpandingWildSpine() {
    try {
      if (!this.expandingWildSpineData) {
        return null;
      }

      const assets = PIXI.Assets.cache;
      const skeletonKey = this.expandingWildSpineData.skeleton;
      const atlasKey = this.expandingWildSpineData.atlas;

      if (!assets.has(skeletonKey) || !assets.has(atlasKey)) {
        return null;
      }

      const expandingWildSpine = Spine.from({
        skeleton: skeletonKey,
        atlas: atlasKey,
      });

      if (
        !expandingWildSpine ||
        !expandingWildSpine.skeleton ||
        !expandingWildSpine.state
      ) {
        return null;
      }

      if (
        !expandingWildSpine.skeleton.data ||
        !expandingWildSpine.skeleton.data.animations
      ) {
        return null;
      }

      return expandingWildSpine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Calculate responsive scale and positioning for expanding wild spine animation
   * @param {Spine} spineInstance - The spine instance to scale
   * @param {number} containerWidth - Available container width
   * @param {number} containerHeight - Available container height (full reel height)
   * @returns {Object} - Scale and positioning information
   * Cached for performance - invalidated when dimensions change
   */
  calculateExpandingWildScale(
    spineInstance,
    containerWidth,
    containerHeight,
    preserveAnimation = false
  ) {
    // Set default animation to get proper bounds (only if not preserving current animation)
    if (
      !preserveAnimation &&
      spineInstance &&
      spineInstance.state &&
      spineInstance.state.data &&
      spineInstance.state.data.skeletonData.animations.length > 0
    ) {
      const defaultAnimation =
        spineInstance.state.data.skeletonData.animations[0];
      spineInstance.state.setAnimation(0, defaultAnimation.name, true);
    }

    // Check cache first
    const cacheKey = `${containerWidth}_${containerHeight}`;
    if (
      this._expandingWildScaleCache &&
      this._expandingWildScaleCache.cacheKey === cacheKey
    ) {
      return this._expandingWildScaleCache.result;
    }

    // Calculate scale to fit within container while maintaining aspect ratio
    const nativeWidth = Settings.SYMBOL_WIDTH;
    const nativeHeight = Settings.SYMBOL_HEIGHT;

    const widthScale = containerWidth / nativeWidth;
    const heightScale = containerHeight / nativeHeight;
    const scale = Math.min(widthScale, heightScale);

    // Calculate final dimensions and centering offsets
    const scaledWidth = nativeWidth * scale;
    const scaledHeight = nativeHeight * scale;
    const x = (containerWidth - scaledWidth) / 2;
    const y = (containerHeight - scaledHeight) / 2;

    const result = {
      scale,
      nativeWidth,
      nativeHeight,
      x,
      y,
      scaledWidth,
      scaledHeight,
    };

    // Cache result
    this._expandingWildScaleCache = {
      cacheKey,
      result,
    };

    return result;
  }

  /**
   * Apply scaling and positioning to expanding wild spine
   */
  applyExpandingWildScaling() {
    if (!this.expandingWildSpine || this.expandingWildSpine.destroyed) {
      return;
    }

    // Get current animation before scaling (to preserve it)
    let currentAnimation = null;
    let currentLoop = false;
    if (this.expandingWildSpine.state) {
      const currentTrack = this.expandingWildSpine.state.getCurrent(0);
      if (currentTrack && currentTrack.animation) {
        currentAnimation = currentTrack.animation.name;
        currentLoop = currentTrack.loop;
      }
    }

    // Calculate full reel height (4 visible symbols)
    const fullReelHeight = this.symbolHeight * GAME_LAYOUT.SYMBOLS_VISIBLE;

    const scaleData = this.calculateExpandingWildScale(
      this.expandingWildSpine,
      this.symbolWidth,
      fullReelHeight,
      true // Preserve animation during resize
    );

    if (this.expandingWildSpine.scale) {
      this.expandingWildSpine.scale.set(
        scaleData.scale,
        scaleData.scale * this.expandingWildHeightMultiplier
      );
    }

    if (this.expandingWildSpine.position) {
      const yOffset = fullReelHeight * this.expandingWildYOffsetMultiplier;
      this.expandingWildSpine.position.set(scaleData.x, scaleData.y + yOffset);
    }

    // Restore the animation that was playing before resize
    if (currentAnimation && this.expandingWildSpine.state) {
      try {
        this.expandingWildSpine.state.setAnimation(
          0,
          currentAnimation,
          currentLoop
        );
      } catch (error) {
        // Silent fail - animation might not exist
      }
    }
  }

  /**
   * Warm up expanding wild spine with all animations (appear, idle, win)
   * Same pattern as warmUpSpineAnimation for symbol spines
   */
  warmUpExpandingWildSpine() {
    if (!this.expandingWildSpine || !this.expandingWildSpine.state) {
      return;
    }

    const animations = ["appear", "idle", "win"];

    animations.forEach((animName) => {
      try {
        // Check if animation exists
        const hasAnim = this.expandingWildSpine.skeleton?.data?.animations?.find(
          (anim) => anim.name === animName
        );

        if (!hasAnim) {
          return;
        }

        // Set animation
        this.expandingWildSpine.state.clearTracks();
        this.expandingWildSpine.state.setAnimation(0, animName, false);

        // CRITICAL: Update one frame to trigger shader compilation
        this.expandingWildSpine.state.update(0.016);

        // CRITICAL: Populate bone/slot hierarchy for GPU
        try {
          if (this.expandingWildSpine.skeleton.physics !== undefined) {
            this.expandingWildSpine.skeleton.updateWorldTransform();
          }
        } catch (transformError) {
          // Skip if physics not initialized
        }

        // Keep at alpha = 0.001 so PixiJS continues rendering it
        this.expandingWildSpine.alpha = 0.001;

        // Clear tracks after warmup frame renders
        requestAnimationFrame(() => {
          if (this.expandingWildSpine?.state) {
            this.expandingWildSpine.state.clearTracks();
            this.expandingWildSpine.alpha = 0.001;
          }
        });
      } catch (error) {
        // Silent fail - warmup is optional optimization
      }
    });
  }

  /**
   * Get available animation names for expanding wild spine
   */
  getExpandingWildAvailableAnimations() {
    if (!this.expandingWildSpine?.skeleton?.data?.animations) {
      return [];
    }
    return this.expandingWildSpine.skeleton.data.animations.map(
      (anim) => anim.name
    );
  }

  /**
   * Initialize expanding wild spine and add to container
   */
  initializeExpandingWildSpine() {
    if (this.expandingWildInitialized || !this.reelContainer) {
      return;
    }

    this.expandingWildSpine = this.createExpandingWildSpine();

    if (!this.expandingWildSpine) {
      return;
    }

    // Validate spine components
    const missingComponents = [];
    if (!this.expandingWildSpine.skeleton) missingComponents.push("skeleton");
    if (!this.expandingWildSpine.state) missingComponents.push("state");
    if (!this.expandingWildSpine.skeleton?.data)
      missingComponents.push("skeleton.data");
    if (!this.expandingWildSpine.skeleton?.slots)
      missingComponents.push("skeleton.slots");
    if (!this.expandingWildSpine.skeleton?.bones)
      missingComponents.push("skeleton.bones");

    if (missingComponents.length > 0) {
      return;
    }

    // Apply x2 skin for middle reel (index 2) - only if multiplier feature is enabled
    if (this.reelIndex === 2 && this.expandingWildSpine.skeleton && FEATURES_CONFIG.EXPANDING_WILDS.MULTIPLIER_ENABLED) {
      try {
        const x2Skin = this.expandingWildSpine.skeleton.data.findSkin("x2");
        if (x2Skin) {
          this.expandingWildSpine.skeleton.setSkin(x2Skin);
          this.expandingWildSpine.skeleton.setSlotsToSetupPose();
        }
      } catch (error) {
        // Fail silently
      }
    }

    this.expandingWildSpine.position.set(0, 0);
    this.expandingWildSpine.zIndex = 15; // Higher than symbol animations
    this.expandingWildSpine.alpha = 0.001; // Hidden initially, keep in render pipeline
    this.isExpandingWildVisible = false;

    this.reelContainer.addChild(this.expandingWildSpine);
    this.applyExpandingWildScaling();

    if (this.expandingWildSpine.parent !== this.reelContainer) {
      return;
    }

    // Minimal warmup - warm up "in" animation since that's what plays first
    this.warmUpExpandingWildSpine();

    this.expandingWildInitialized = true;
  }

  /**
   * Clear all active timeouts for expanding wild animations
   */
  clearExpandingWildTimeouts() {
    this.expandingWildTimeouts.forEach(clearTimeout);
    this.expandingWildTimeouts = [];
  }

  /**
   * Hide expanding wild animation immediately (synchronous, same as regular win animations)
   */
  hideExpandingWildFullReel() {
    if (!this.expandingWildSpine || this.expandingWildSpine.destroyed) {
      this.isExpandingWildVisible = false;
      this.currentExpandingWildAnimation = null;
      return;
    }

    // Synchronous cleanup - same pattern as hideWinAnimation()
    this.expandingWildSpine.alpha = 0.001; // Keep in render pipeline
    this.expandingWildSpine.autoUpdate = false;
    this.isExpandingWildVisible = false;
    this.currentExpandingWildAnimation = null;

    if (this.expandingWildSpine.state) {
      try {
        this.expandingWildSpine.state.clearTracks();
        // Use setBonesToSetupPose() instead of setToSetupPose() — resets bones without slot attachment changes (no DarkSlotMesh leak)
        this.expandingWildSpine.skeleton.setBonesToSetupPose();
      } catch (error) {
        // Silent fail
      }
    }
  }

  /**
   * Force stop expanding wild animations (synchronous, same as regular win animations)
   */
  forceStopExpandingWildFullReel() {
    this.clearExpandingWildTimeouts();
    // Direct synchronous cleanup - same pattern as regular animations
    this.hideExpandingWildFullReel();
  }

  /**
   * Play expanding wild full-reel animation (appear animation, then idle)
   */
  playExpandingWildFullReel() {
    // Lazy initialization if not already initialized
    if (!this.expandingWildInitialized) {
      this.initializeExpandingWildSpine();
    }

    if (
      !this.expandingWildSpine ||
      this.expandingWildSpine.destroyed ||
      !this.expandingWildSpine.state
    ) {
      return;
    }

    requestAnimationFrame(() => {
      if (
        !this.expandingWildSpine ||
        this.expandingWildSpine.destroyed ||
        !this.expandingWildSpine.state
      ) {
        return;
      }

      try {
        this.expandingWildSpine.state.clearTracks();
        this.expandingWildSpine.state.clearListeners();
        this.expandingWildSpine.state.apply(this.expandingWildSpine.skeleton);
        // Use setBonesToSetupPose() instead of setToSetupPose() — resets bones without slot attachment changes (no DarkSlotMesh leak)
        this.expandingWildSpine.skeleton.setBonesToSetupPose();

        this.expandingWildSpine.alpha = 1; // Show
        this.expandingWildSpine.autoUpdate = true;
        this.isExpandingWildVisible = true;

        // Sound is played centrally in useWinController
        // Middle reel (x2 multiplier) plays additional sound when entering idle

        // Play "appear" animation (non-looping), then transition to "idle" (looping)
        this.expandingWildSpine.state.setAnimation(0, "appear", false);
        this.expandingWildSpine.state.addAnimation(0, "idle", true, 0);
        this.currentExpandingWildAnimation = "appear";

        // Add listener for when "appear" completes and "idle" starts
        // Play multiplier sound for middle reel (reel 2 with x2 skin) - only if multiplier feature is enabled
        if (this.reelIndex === 2 && FEATURES_CONFIG.EXPANDING_WILDS.MULTIPLIER_ENABLED) {
          const completeListener = {
            complete: (entry) => {
              if (entry.animation.name === "appear") {
                soundManager.play(SOUND_NAME.expanding_wild_multiplier);

                // Remove listener after playing once
                this.expandingWildSpine.state.removeListener(completeListener);
              }
            },
          };
          this.expandingWildSpine.state.addListener(completeListener);
        }
      } catch (error) {
        // Silent fail
        this.hideExpandingWildFullReel();
      }
    });
  }

  /**
   * Play expanding wild win animation (switches from idle to win)
   */
  playExpandingWildFullReelWin() {
    if (
      !this.expandingWildSpine ||
      this.expandingWildSpine.destroyed ||
      !this.expandingWildSpine.state ||
      !this.isExpandingWildVisible
    ) {
      return;
    }

    this._pendingRAF = requestAnimationFrame(() => {
      this._pendingRAF = null;
      if (
        !this.expandingWildSpine ||
        this.expandingWildSpine.destroyed ||
        !this.expandingWildSpine.state
      ) {
        return;
      }

      try {
        // Switch to win animation (looping) - transitions smoothly from current animation
        this.expandingWildSpine.state.setAnimation(0, "win", true);
        this.currentExpandingWildAnimation = "win";
      } catch (error) {
        // Silent fail
      }
    });
  }

  /**
   * Play dark animation for expanding wild (indicates it's not part of current line)
   */
  playExpandingWildDarkAnimation() {
    if (
      !this.expandingWildSpine ||
      this.expandingWildSpine.destroyed ||
      !this.expandingWildSpine.state ||
      !this.isExpandingWildVisible
    ) {
      return;
    }

    try {
      // Switch to dark animation (play once, hold at last frame)
      this.expandingWildSpine.state.setAnimation(0, "dark", false);
      this.currentExpandingWildAnimation = "dark";
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Play win animation for expanding wild (restore normal appearance)
   */
  playExpandingWildWinAnimation() {
    if (
      !this.expandingWildSpine ||
      this.expandingWildSpine.destroyed ||
      !this.expandingWildSpine.state ||
      !this.isExpandingWildVisible
    ) {
      return;
    }

    try {
      // Switch to win animation (looping)
      this.expandingWildSpine.state.setAnimation(0, "win", true);
      this.currentExpandingWildAnimation = "win";
    } catch (error) {
      // Silent fail
    }
  }

  cleanup() {
    // Cancel any pending requestAnimationFrame
    if (this._pendingRAF) {
      cancelAnimationFrame(this._pendingRAF);
      this._pendingRAF = null;
    }

    this.hideAllAnimations();
    this.hideExpandingWildFullReel();
    this.clearExpandingWildTimeouts();

    // Cleanup all coin value texts
    this.hideAllCoinValueTexts();

    for (const spineInstance of this.prePositionedSpines.values()) {
      if (spineInstance) {
        if (spineInstance.state) spineInstance.state.clearTracks();
        if (spineInstance.parent) {
          spineInstance.parent.removeChild(spineInstance);
        }
        spineInstance.destroy({
          children: true,
          texture: false,
          baseTexture: false,
        });
      }
    }

    // Cleanup expanding wild spine
    if (this.expandingWildSpine && !this.expandingWildSpine.destroyed) {
      try {
        if (this.expandingWildSpine.state) {
          this.expandingWildSpine.state.clearTracks();
          this.expandingWildSpine.state.clearListeners();
        }

        if (this.expandingWildSpine.parent) {
          this.expandingWildSpine.parent.removeChild(this.expandingWildSpine);
        }

        this.expandingWildSpine.destroy({
          children: true,
          texture: false,
          baseTexture: false,
        });
      } catch (error) {
        // Silent fail
      }
    }

    this.prePositionedSpines.clear();
    this.activeAnimations.clear();
    this.animationCache.clear();
    this.animationAvailability.clear();
    this.coinValueContainers.clear();
    this.expandingWildSpine = null;
    this.expandingWildInitialized = false;
    this.isExpandingWildVisible = false;
    this.currentExpandingWildAnimation = null;
  }
}

export default OverlayReelController;
